import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import apiClient from '../api/apiClient';
import quranApi from '../api/quranApi';
import { useDailyActions } from '../context/DailyActionsContext';
import { useTheme } from '../context/ThemeContext';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import HadithOfTheDay from '../components/common/HadithOfTheDay';
import { FaTasks, FaCheckDouble, FaQuran } from 'react-icons/fa';

ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    Title
);

const Dashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { dailyData: dailyActions } = useDailyActions(); // Rename for compatibility
    const { theme } = useTheme();
    const [tasks, setTasks] = useState([]);
    const [quranProgress, setQuranProgress] = useState(null);
    const [surahName, setSurahName] = useState('');

    // Define colors based on theme
    const getThemeColors = (activeTheme) => {
        if (activeTheme === 'girly') {
            return {
                primary: '#E84393',
                secondary: '#FAB1A0',
                text: '#2D3436',
                chart: {
                    completed: '#E84393',
                    pending: '#FAB1A0',
                }
            };
        } else if (activeTheme === 'neutral') {
            return {
                primary: '#2D3436',
                secondary: '#636E72',
                text: '#2D3436',
                chart: {
                    completed: '#2D3436',
                    pending: '#B2BEC3',
                }
            };
        }
        // Default
        return {
            primary: '#1B7A6E',
            secondary: '#D4AF37',
            text: '#2D3436',
            chart: {
                completed: '#1B7A6E',
                pending: '#D4AF37', // Gold for pending in default theme usually looks nice or consistent
            }
        };
    };

    const themeColors = getThemeColors(theme);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const tasksRes = await apiClient.get('/tasks');
                setTasks(Array.isArray(tasksRes.data) ? tasksRes.data : []);

                // dailyActions is now handled by context

                const quranRes = await apiClient.get('/quran/stats');
                setQuranProgress(quranRes.data);

                // Fetch Surah Name if progress exists
                if (quranRes.data && quranRes.data.lastReadSurah) {
                    try {
                        // Ideally we should cache this list or store it context, but for now fetching it is okay
                        const allSurahs = await quranApi.getAllSurahs();
                        const currentSurah = allSurahs.find(s => s.number === quranRes.data.lastReadSurah);
                        if (currentSurah) {
                            setSurahName(currentSurah.englishName);
                        }
                    } catch (err) {
                        console.error("Failed to fetch surah name", err);
                    }
                }

            } catch (error) {
                console.error('Error fetching dashboard data', error);
            }
        };

        fetchData();
    }, []);

    // --- Task Stats (Donut) ---
    const completedTasks = tasks.filter((task) => task.completed).length;
    const pendingTasks = tasks.length - completedTasks;
    const totalTasks = tasks.length;
    const taskCompletionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    const taskData = {
        labels: ['Terminées', 'En attente'],
        datasets: [
            {
                data: [completedTasks, pendingTasks],
                // backgroundColor: ['#00B894', themeColors.chart.pending], // Removed duplicate
                backgroundColor: [themeColors.chart.completed, themeColors.chart.pending],
                hoverBackgroundColor: [themeColors.chart.completed, themeColors.chart.pending],
                borderWidth: 0,
            },
        ],
    };

    const taskOptions = {
        cutout: '70%',
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    font: { family: 'Poppins', size: 12 },
                },
            },
        },
    };

    // --- Daily Actions Stats (Bar) ---
    const actionStats = dailyActions?.actions?.reduce(
        (acc, action) => {
            if (action.completed) acc.completed++;
            else acc.pending++;
            return acc;
        },
        { completed: 0, pending: 0 }
    ) || { completed: 0, pending: 0 };

    const dailyData = {
        labels: ['Actions'],
        datasets: [
            {
                label: 'Accomplies',
                data: [actionStats.completed],
                backgroundColor: themeColors.chart.completed,
                borderRadius: 10,
                barThickness: 40,
            },
            {
                label: 'En attente',
                data: [actionStats.pending],
                backgroundColor: '#E0E0E0',
                borderRadius: 10,
                barThickness: 40,
            },
        ],
    };

    const dailyOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
        },
        scales: {
            x: { grid: { display: false }, stacked: true },
            y: { display: false, stacked: true, max: 5 },
        },
    };

    return (
        <div className="dashboard-container page-transition">
            {/* Header is now separate component, but we keep the grid layout */}

            <div className="stats-grid">
                {/* Widget 1: Tâches */}
                <div
                    className="card dashboard-card"
                    onClick={() => navigate('/tasks')}
                    style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                    <h3>
                        <FaTasks style={{ color: themeColors.secondary }} /> Progression Tâches
                    </h3>
                    <div className="chart-container" style={{ position: 'relative' }}>
                        {tasks.length > 0 ? (
                            <>
                                <Doughnut data={taskData} options={taskOptions} />
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -65%)',
                                        textAlign: 'center',
                                    }}
                                >
                                    <span style={{ fontSize: '2rem', fontWeight: 'bold', fontFamily: 'Playfair Display', color: themeColors.primary }}>
                                        {taskCompletionRate}%
                                    </span>
                                </div>
                            </>
                        ) : (
                            <p style={{ textAlign: 'center', color: '#888', marginTop: '40px' }}>
                                Aucune tâche
                            </p>
                        )}
                    </div>
                </div>

                {/* Widget 2: Actions du Jour */}
                <div className="card dashboard-card">
                    <h3>
                        <FaCheckDouble style={{ color: themeColors.primary }} /> Actions du Jour
                    </h3>
                    <p className="card-subtitle">
                        {actionStats.completed} / {actionStats.completed + actionStats.pending} accomplies
                    </p>
                    <div className="chart-container">
                        <Bar data={dailyData} options={dailyOptions} />
                    </div>
                </div>

                {/* Widget 3: Coran */}
                <div
                    className="card dashboard-card"
                    onClick={() => navigate('/quran')}
                    style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                    <h3>
                        <FaQuran style={{ color: themeColors.primary }} /> Progression Coran
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#636E72' }}>Dernière lecture</span>
                            <span style={{ fontWeight: 'bold', color: themeColors.primary, fontSize: '1.1rem' }}>
                                {surahName || `Sourate ${quranProgress?.lastReadSurah || 1}`}
                            </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#636E72' }}>Verset</span>
                            <span style={{ fontWeight: 'bold', color: themeColors.primary, fontSize: '1.1rem' }}>
                                {quranProgress?.lastReadAyah || 1}
                            </span>
                        </div>
                        <div style={{ marginTop: '10px', padding: '15px', background: '#F8F9FA', borderRadius: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                <span style={{ fontSize: '0.9rem' }}>Sourates terminées</span>
                                <span style={{ fontWeight: 'bold' }}>{quranProgress?.completedSurahsCount || 0} / 114</span>
                            </div>
                            <div style={{ height: '6px', background: '#E0E0E0', borderRadius: '3px', overflow: 'hidden' }}>
                                <div
                                    style={{
                                        width: `${((quranProgress?.completedSurahsCount || 0) / 114) * 100}%`,
                                        height: '100%',
                                        background: themeColors.secondary,
                                    }}
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Widget 4: Hadith (Full Width) */}
                <div className="card full-width" style={{ padding: 0, overflow: 'hidden', border: 'none', boxShadow: 'none', background: 'transparent' }}>
                    <HadithOfTheDay />
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
